import React, { useState } from "react";
import { Box, Rating, Typography } from "@mui/material";
import { Button, TextField, Snackbar } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { supabase } from "../../../../../supabase";

const useStyles = makeStyles(theme => ({
  submitButton: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * Component to allow a user to rate an Agent Assist service.
 *
 * @param {object} props - The component props.
 * @param {number} props.ticketId - The ID of the ticket to rate.
 * @param {Function} props.onClose - A function to call when the modal is closed.
 * @param {string} props.table - The table to update in Supabase.
 *
 * @returns {React.ReactElement} - The RateAgentAssistServiceModal component.
 */
export default function RateAgentAssistServiceModal({
  ticketId,
  onClose,
  table,
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const classes = useStyles();

  const handleSubmit = async () => {
    setSubmitting(true);
    const { error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from(table)
      .update({ rating_comment: comment, rating_star: rating })
      .eq("id", ticketId)
      .select();

    setIsSnackbarOpen(true);
    setTimeout(() => {
      setRating(0);
      setComment("");
      setSubmitting(false);
      onClose();
    }, 1500);

    if (error) {
      console.error(error.message);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: 3,
      }}
    >
      <Typography variant="h6">Rate your experience</Typography>
      <Rating
        name="user-rating"
        value={rating}
        onChange={(event, newValue) => setRating(newValue)}
      />

      <TextField
        label="Leave a comment"
        multiline
        fullWidth
        minRows={4}
        value={comment}
        onChange={e => setComment(e.target.value)}
        variant="outlined"
      />

      <Button
        color="secondary"
        variant="contained"
        disabled={rating === 0}
        onClick={handleSubmit}
        className={classes.submitButton}
      >
        {submitting ? "Loading..." : "Submit Feedback"}
      </Button>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setIsSnackbarOpen(false)}
      >
        <Alert severity="success" onClose={() => setIsSnackbarOpen(false)}>
          Service rated successfully
        </Alert>
      </Snackbar>
    </Box>
  );
}
